/** SPEC-FE-002 — cliente HTTP da aplicação.
 *
 * Concentra o que toda requisição precisa fazer igual: mandar o token,
 * carimbar o `X-Request-Id`, renovar a sessão quando ela expira, gerar
 * `Idempotency-Key` nas escritas e traduzir erro para tipo.
 *
 * O access token vive **em memória**, nunca em `localStorage`: um XSS que
 * alcance o storage leva a sessão junto. O refresh vive em cookie `HttpOnly`,
 * que o JavaScript não enxerga — é o navegador que o envia.
 */

import { ApiError, FalhaDeRede, interpretarErro } from "./problem";

const TIMEOUT_PADRAO_MS = 30_000;
const TIMEOUT_UPLOAD_MS = 120_000;
const METODOS_IDEMPOTENTES = new Set(["GET", "HEAD", "PUT", "DELETE"]);
const METODOS_COM_CHAVE = new Set(["POST", "PATCH", "DELETE"]);
const MAX_TENTATIVAS = 3;

/** Rotas de sessão. Um 401 aqui é resposta legítima, não sessão expirada —
 * tentar renovar produziria recursão. */
const ROTAS_DE_SESSAO = ["/auth/login", "/auth/signup", "/auth/refresh", "/auth/logout"];

/** O que aconteceu com a tentativa de renovar.
 *
 * Três estados, e não um booleano, porque duas das saídas eram tratadas como a
 * mesma: qualquer falha do `POST /auth/refresh` zerava o token e mandava a
 * pessoa para o login. Um `502` durante a troca de slot do blue/green, um
 * timeout, uma queda de rede de dois segundos — todos derrubavam uma sessão que
 * continuava perfeitamente válida, com o cookie de refresh intacto no
 * navegador.
 */
type ResultadoDaRenovacao =
  /** Token novo em mãos: dá para repetir a requisição. */
  | "renovada"
  /** O servidor recusou o refresh (401/403). A sessão acabou de verdade. */
  | "expirada"
  /** Não deu para saber: 5xx, 429, timeout, rede. A sessão continua de pé. */
  | "indisponivel";

function ehRotaDeSessao(rota: string): boolean {
  return ROTAS_DE_SESSAO.some((r) => rota.startsWith(r));
}

export interface OpcoesDeRequisicao extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Upload precisa de mais tempo que uma chamada comum. */
  timeoutMs?: number;
  /** Repetir esta requisição não deve gerar efeito duplicado. */
  idempotencyKey?: string;
  /** Uso interno: impede que o refresh tente se renovar. */
  semRenovacao?: boolean;
}

function novoId(): string {
  // `randomUUID` exige contexto seguro. Em HTTP simples — desenvolvimento em
  // rede local, por exemplo — ele não existe, e o identificador de requisição
  // não pode ser o que impede a aplicação de funcionar.
  const cripto = globalThis.crypto as Partial<Crypto> | undefined;
  if (cripto !== undefined && typeof cripto.randomUUID === "function") return cripto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function esperar(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class ClienteHttp {
  #baseUrl: string;
  #accessToken: string | null = null;

  /** Renovação em andamento. Enquanto existe, requisições que tomaram 401
   * aguardam esta promessa em vez de disparar renovações concorrentes — que
   * é como se perde uma sessão: a primeira rotação invalida o token que as
   * outras estão apresentando, e o backend trata isso como reuso. */
  #renovacaoEmVoo: Promise<ResultadoDaRenovacao> | null = null;

  /** Avisado quando a sessão cai de vez. A aplicação usa para levar ao login. */
  aoPerderSessao: (() => void) | null = null;

  constructor(baseUrl: string) {
    this.#baseUrl = baseUrl.replace(/\/$/, "");
  }

  get autenticado(): boolean {
    return this.#accessToken !== null;
  }

  definirToken(token: string | null): void {
    this.#accessToken = token;
  }

  // ------------------------------------------------------------------ //

  async get<T>(rota: string, opcoes: OpcoesDeRequisicao = {}): Promise<T> {
    return this.#executar<T>("GET", rota, opcoes);
  }

  async post<T>(rota: string, body?: unknown, opcoes: OpcoesDeRequisicao = {}): Promise<T> {
    return this.#executar<T>("POST", rota, { ...opcoes, body });
  }

  async patch<T>(rota: string, body?: unknown, opcoes: OpcoesDeRequisicao = {}): Promise<T> {
    return this.#executar<T>("PATCH", rota, { ...opcoes, body });
  }

  async put<T>(rota: string, body?: unknown, opcoes: OpcoesDeRequisicao = {}): Promise<T> {
    return this.#executar<T>("PUT", rota, { ...opcoes, body });
  }

  async delete<T>(rota: string, opcoes: OpcoesDeRequisicao = {}): Promise<T> {
    return this.#executar<T>("DELETE", rota, opcoes);
  }

  /** Abre uma resposta que não termina — o stream de eventos (SPEC-FE-001 § 3).
   *
   * Aqui e não no chamador porque **o token não sai desta classe**. A
   * alternativa óbvia é o `EventSource` do navegador, que não manda cabeçalho
   * e obrigaria a pôr o access token na URL: log do nginx, histórico do
   * navegador e `Referer`. O preço de ficar aqui é escrever a reconexão à
   * mão; o preço da outra escolha é uma credencial em texto no disco.
   *
   * Sem prazo: quem controla o fim é o `signal` de quem chamou. O relógio de
   * 30 s existe para requisição que devia ter respondido, e uma resposta que
   * dura horas é o ponto desta.
   */
  async abrirStream(rota: string, signal: AbortSignal): Promise<Response> {
    return this.#comRenovacao("GET", rota, {
      signal,
      headers: { Accept: "text/event-stream" },
    });
  }

  // ------------------------------------------------------------------ //

  async #executar<T>(metodo: string, rota: string, opcoes: OpcoesDeRequisicao): Promise<T> {
    // A chave nasce **uma vez por operação**, e não por tentativa.
    //
    // Gerada lá embaixo, no `#enviar`, cada repetição e o replay que acontece
    // depois de renovar a sessão saíam com uma chave nova — e uma chave nova é,
    // para o servidor, uma operação diferente. A proteção que o retry invoca
    // simplesmente não existia: o mesmo POST podia executar duas vezes.
    const comChave =
      METODOS_COM_CHAVE.has(metodo) && !ehRotaDeSessao(rota)
        ? { ...opcoes, idempotencyKey: opcoes.idempotencyKey ?? novoId() }
        : opcoes;

    const resposta = await this.#comRenovacao(metodo, rota, comChave);

    if (resposta.status === 204) return undefined as T;
    if (!resposta.ok) throw await interpretarErro(resposta);

    const tipo = resposta.headers.get("Content-Type") ?? "";
    return tipo.includes("json") ? ((await resposta.json()) as T) : (undefined as T);
  }

  /** Envia; se tomar 401 e houver como renovar, renova e repete uma vez. */
  async #comRenovacao(metodo: string, rota: string, opcoes: OpcoesDeRequisicao): Promise<Response> {
    const resposta = await this.#comRepeticao(metodo, rota, opcoes);

    const renovavel = resposta.status === 401 && !opcoes.semRenovacao && !ehRotaDeSessao(rota);

    if (!renovavel) return resposta;

    const resultado = await this.#renovarSessao();
    if (resultado === "renovada") return this.#comRepeticao(metodo, rota, opcoes);

    // Só a recusa do servidor derruba a sessão. Indisponibilidade devolve o 401
    // original para quem chamou: a tela mostra o erro, a pessoa continua
    // logada, e a próxima tentativa renova normalmente quando o servidor voltar.
    if (resultado === "expirada") this.aoPerderSessao?.();
    return resposta;
  }

  /** Uma renovação por vez, compartilhada por todos os chamadores. */
  async #renovarSessao(): Promise<ResultadoDaRenovacao> {
    this.#renovacaoEmVoo ??= this.#pedirNovoToken().finally(() => {
      this.#renovacaoEmVoo = null;
    });
    return this.#renovacaoEmVoo;
  }

  async #pedirNovoToken(): Promise<ResultadoDaRenovacao> {
    try {
      const resposta = await this.#enviar("POST", "/auth/refresh", { semRenovacao: true });
      if (resposta.ok) {
        const { access_token } = (await resposta.json()) as { access_token: string };
        this.#accessToken = access_token;
        return "renovada";
      }
      // 401/403 é o servidor dizendo que o refresh não vale mais — token
      // expirado, rotacionado ou revogado. Qualquer outro status é o servidor
      // não conseguindo responder, e o cookie de refresh continua válido.
      if (resposta.status === 401 || resposta.status === 403) {
        this.#accessToken = null;
        return "expirada";
      }
      return "indisponivel";
    } catch {
      // Timeout ou rede: não se sabe nada sobre a sessão, e presumir o pior
      // desloga quem está só com a internet ruim.
      return "indisponivel";
    }
  }

  /** Repete falha transitória — só em método idempotente.
   *
   * Repetir um POST às cegas duplicaria efeito. Onde a repetição importa, a
   * `Idempotency-Key` já protege, e é ela que torna seguro o cliente insistir.
   */
  async #comRepeticao(metodo: string, rota: string, opcoes: OpcoesDeRequisicao): Promise<Response> {
    const podeRepetir = METODOS_IDEMPOTENTES.has(metodo);
    let ultimaFalha: unknown;

    for (let tentativa = 1; tentativa <= (podeRepetir ? MAX_TENTATIVAS : 1); tentativa++) {
      try {
        const resposta = await this.#enviar(metodo, rota, opcoes);

        // 5xx e 429 são transitórios; 4xx é o servidor dizendo que o pedido
        // está errado — insistir não muda a resposta.
        const transitorio = resposta.status >= 500 || resposta.status === 429;
        if (!transitorio || !podeRepetir || tentativa === MAX_TENTATIVAS) return resposta;
      } catch (falha) {
        ultimaFalha = falha;
        if (!podeRepetir || tentativa === MAX_TENTATIVAS) throw falha;
      }

      await esperar(2 ** (tentativa - 1) * 250);
    }

    throw ultimaFalha instanceof Error
      ? ultimaFalha
      : new FalhaDeRede("Falha após todas as tentativas.");
  }

  async #enviar(metodo: string, rota: string, opcoes: OpcoesDeRequisicao): Promise<Response> {
    const { body, timeoutMs, idempotencyKey, semRenovacao, headers, ...resto } = opcoes;
    void semRenovacao;

    const cabecalhos = new Headers(headers);
    cabecalhos.set("X-Request-Id", novoId());
    // Só quando quem chamou não disse outra coisa: o stream de eventos pede
    // `text/event-stream`, e sobrescrever aqui faria o servidor negociar o
    // tipo errado.
    if (!cabecalhos.has("Accept")) cabecalhos.set("Accept", "application/json");

    if (this.#accessToken) {
      cabecalhos.set("Authorization", `Bearer ${this.#accessToken}`);
    }

    // O caminho normal já traz a chave de `#executar`, uma por operação. O
    // fallback aqui cobre quem chama `#enviar` direto (o refresh, por exemplo)
    // e garante que nenhuma escrita saia sem a chave — o servidor a exige em
    // toda escrita autenticada (SPEC-PLAT-001), e esquecê-la vira 400 na cara
    // do usuário por um detalhe de protocolo.
    if (METODOS_COM_CHAVE.has(metodo) && !ehRotaDeSessao(rota)) {
      cabecalhos.set("Idempotency-Key", idempotencyKey ?? novoId());
    }

    let corpo: BodyInit | undefined;
    if (body !== undefined) {
      if (body instanceof FormData) {
        corpo = body; // o navegador define o boundary do multipart
      } else {
        cabecalhos.set("Content-Type", "application/json");
        corpo = JSON.stringify(body);
      }
    }

    const limite = timeoutMs ?? (body instanceof FormData ? TIMEOUT_UPLOAD_MS : TIMEOUT_PADRAO_MS);
    const relogio = AbortSignal.timeout(limite);

    const destino = `${this.#baseUrl}${rota}`;

    try {
      return await fetch(destino, {
        ...resto,
        method: metodo,
        headers: cabecalhos,
        body: corpo,
        // Sem isto o cookie de refresh não viaja: o front e a API são origens
        // distintas (`crm.` e `crm-api.`), ainda que do mesmo site.
        credentials: "include",
        signal: resto.signal ?? relogio,
      });
    } catch (causa) {
      if (causa instanceof DOMException && causa.name === "TimeoutError") {
        throw new FalhaDeRede(`A requisição passou de ${limite / 1000}s sem resposta.`, causa);
      }
      throw new FalhaDeRede("Não foi possível falar com o servidor.", causa);
    }
  }
}

export { ApiError, FalhaDeRede };

/** Endereço da API. Cada instância aponta para o seu próprio host — ver
 * `.env.example`. O padrão serve ao desenvolvimento local. */
const API_URL: string =
  (import.meta.env as Record<string, string | undefined>)["VITE_API_URL"] ??
  "http://localhost:8000/api/v1";

export const http = new ClienteHttp(API_URL);
